import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Card, CardTitle, Heading, IconClock } from '@snsw-gel/react';
import { useEventFlow } from 'app/contexts/EventFlowContext';
import EventLayout from './EventLayout';
import {
  ConfirmationHeader,
  StyledTickIcon,
  StyledHeading,
  NotificationText,
  EventCardWrapper,
  ManageText,
  TutdWidget,
  EventDetailsBody,
  EventTypeLabel,
} from './EventConfirmation.styled';

interface EventInfo {
  title?: string;
  eventStartTime?: string;
  eventEndTime?: string;
  eventType?: string;
  location?: string;
  thumbsUpThumbsDownId?: string | null;
}

const formatEventDateTime = (startTime?: string, endTime?: string): string => {
  if (!startTime) return '';
  const start = new Date(startTime);
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  let formatted = start.toLocaleDateString('en-AU', options);
  if (endTime) {
    const end = new Date(endTime);
    const endFormatted = end.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    formatted += ` - ${endFormatted}`;
  }
  return formatted;
};

const EventConfirmation = () => {
  const history = useHistory();
  const { bookingConfirmation, personalDetails, eventDetails, eventId } = useEventFlow();
  const eventInfo: EventInfo | null = eventDetails;

  useEffect(() => {
    try {
      // @ts-ignore
      quickFeed.init({
        placeholder: 'event-tutd-widget',
        label: 'How was your online experience today?',
        environment: process.env.ENV === 'prod' ? 'production' : 'uat',
        sourceApplication: eventInfo?.thumbsUpThumbsDownId || 'servicebooker.events',
      });
    } catch (err) {
      return;
    }
  }, []);

  const handleManageBooking = () => {
    history.push(`/events/${eventId}/manage/${bookingConfirmation?.id}`);
  };

  const notificationText = personalDetails
    ? `We have sent a confirmation email to ${personalDetails.email}${personalDetails.phone ? ` and SMS to ${personalDetails.phone}` : ''}.`
    : '';

  return (
    <EventLayout showStepper={false}>
      <ConfirmationHeader>
        <StyledTickIcon size="md" />
        <StyledHeading level={2}>Your registration is confirmed</StyledHeading>
      </ConfirmationHeader>

      <NotificationText>
        <p>{notificationText}</p>
        <p>You&apos;ll receive relevant updates via email or SMS leading up to the day.</p>
        <p>We hope you enjoy the event.</p>
      </NotificationText>

      {/* Event Details */}
      <div>
        <Heading level={3}>Event details</Heading>
        <EventCardWrapper>
          <Card>
            {eventInfo?.eventType && <EventTypeLabel>{eventInfo.eventType}</EventTypeLabel>}
            <CardTitle level={4}>{eventInfo?.title || 'Event'}</CardTitle>
            {eventInfo?.eventType === 'In-Person' && eventInfo?.location && <p>{eventInfo.location}</p>}
            <EventDetailsBody>
              <IconClock size={'md'} />
              {formatEventDateTime(eventInfo?.eventStartTime, eventInfo?.eventEndTime)}
            </EventDetailsBody>
          </Card>
        </EventCardWrapper>
      </div>

      {/* Manage booking link */}
      <ManageText>
        If you can no longer attend, please{' '}
        <Button variant="link" onClick={handleManageBooking}>
          update
        </Button>{' '}
        your existing booking.
      </ManageText>

      {/* Thumbs Up / Thumbs Down feedback widget */}
      <TutdWidget id="event-tutd-widget" />
    </EventLayout>
  );
};

export default EventConfirmation;
